import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Image, Calendar, Newspaper, ArrowRight, X, Play, Eye, Video } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent } from '../components/ui/dialog';
import axios from 'axios';
import PageSEO from '../components/PageSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

export const MediasPage = () => {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [newsRes, eventsRes, galleryRes, videosRes] = await Promise.all([
        axios.get(`${API}/news?limit=20`),
        axios.get(`${API}/events?limit=20`),
        axios.get(`${API}/gallery?limit=50`),
        axios.get(`${API}/videos?limit=50`),
      ]);
      setNews(newsRes.data);
      setEvents(eventsRes.data);
      setGallery(galleryRes.data);
      setVideos(videosRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Fix image URL for uploaded images
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api/uploads/') || url.startsWith('/api/files/')) return `${BASE_URL}${url}`;
    if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
      return url.replace('/uploads/', '/api/uploads/');
    }
    return url;
  };

  return (
    <main data-testid="medias-page">
      <PageSEO
        title="Médias & Actualités"
        description="Actualités, galerie photos et agenda des événements de la commune de Gandiaye."
        path="/medias"
      />
      {/* Hero Section - Vibrant Gradient */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 py-20 md:py-28">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-400/20 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-blue-400/10 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2" />
        
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6">
              Espace Médias
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              Médias & Actualités
            </h1>
            <p className="text-white/80 text-lg md:text-xl max-w-2xl">
              Découvrez notre galerie photos, les dernières actualités et l'agenda des événements de la commune de Gandiaye.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-neutral-50 to-white">
        <div className="container-main">
          <Tabs defaultValue="actualites" className="w-full">
            {/* Modern Tabs */}
            <TabsList className="w-full flex flex-wrap justify-center gap-2 bg-white p-2 rounded-2xl shadow-lg shadow-neutral-200/50 mb-10 border border-neutral-100">
              <TabsTrigger 
                value="actualites" 
                className="flex-1 md:flex-none px-6 py-3 rounded-xl font-medium transition-all data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-emerald-500/30"
                data-testid="tab-actualites"
              >
                <Newspaper className="w-4 h-4 mr-2" />
                Actualités
              </TabsTrigger>
              <TabsTrigger 
                value="agenda" 
                className="flex-1 md:flex-none px-6 py-3 rounded-xl font-medium transition-all data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-400 data-[state=active]:to-amber-500 data-[state=active]:text-neutral-900 data-[state=active]:shadow-lg data-[state=active]:shadow-amber-400/30"
                data-testid="tab-agenda"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Agenda
              </TabsTrigger>
              <TabsTrigger 
                value="galerie" 
                className="flex-1 md:flex-none px-6 py-3 rounded-xl font-medium transition-all data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-blue-500/30"
                data-testid="tab-galerie"
              >
                <Image className="w-4 h-4 mr-2" />
                Galerie Photos
              </TabsTrigger>
              <TabsTrigger 
                value="videos" 
                className="flex-1 md:flex-none px-6 py-3 rounded-xl font-medium transition-all data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-red-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-red-500/30"
                data-testid="tab-videos"
              >
                <Video className="w-4 h-4 mr-2" />
                Vidéos
              </TabsTrigger>
            </TabsList>

            {/* Actualités Tab */}
            <TabsContent value="actualites">
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-1.5 h-10 bg-gradient-to-b from-emerald-400 to-emerald-600 rounded-full" />
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-neutral-800">
                    Dernières Actualités
                  </h2>
                </div>

                {loading ? (
                  <div className="flex justify-center py-16">
                    <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-500 rounded-full animate-spin" />
                  </div>
                ) : news.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {news.map((item, index) => (
                      <Link to={`/actualites/${item.id}`} key={item.id}>
                        <Card 
                          className="group bg-white rounded-2xl overflow-hidden border-0 shadow-lg shadow-neutral-200/50 hover:shadow-2xl hover:shadow-emerald-500/10 transition-all duration-500 hover:-translate-y-2"
                        >
                          {item.image_url && (
                            <div className="relative aspect-[16/10] overflow-hidden">
                              <img 
                                src={getImageUrl(item.image_url)} 
                                alt={item.title || 'Actualité de Gandiaye'}
                                loading="lazy"
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                              {item.video_url && (
                                <div className="absolute top-3 right-3 w-8 h-8 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center">
                                  <Play size={14} className="text-white ml-0.5" fill="white" />
                                </div>
                              )}
                            </div>
                          )}
                          <CardContent className="p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="px-3 py-1 bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 text-xs font-semibold rounded-full uppercase tracking-wide">
                                {item.category || 'Général'}
                              </span>
                              <span className="text-xs text-neutral-400">
                                {formatDate(item.created_at)}
                              </span>
                            </div>
                            <h3 className="font-heading font-bold text-lg text-neutral-800 mb-2 line-clamp-2 group-hover:text-emerald-600 transition-colors">
                              {item.title}
                            </h3>
                            <p className="text-neutral-600 text-sm line-clamp-2 mb-4">
                              {item.summary || item.content?.substring(0, 100)}
                            </p>
                            <span className="flex items-center gap-2 text-emerald-600 font-semibold text-sm group/btn">
                              Lire la suite
                              <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                            </span>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl">
                    <Newspaper className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                    <p className="text-neutral-600 font-medium">Aucune actualité pour le moment.</p>
                    <p className="text-sm text-neutral-400 mt-2">Les nouvelles seront publiées prochainement.</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Agenda Tab */}
            <TabsContent value="agenda">
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-1.5 h-10 bg-gradient-to-b from-amber-400 to-amber-600 rounded-full" />
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-neutral-800">
                    Agenda des Événements
                  </h2>
                </div>

                {loading ? (
                  <div className="flex justify-center py-16">
                    <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
                  </div>
                ) : events.length > 0 ? (
                  <div className="space-y-4">
                    {events.map((event) => (
                      <Card 
                        key={event.id} 
                        className="bg-white rounded-2xl border-0 shadow-lg shadow-neutral-200/50 hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-300 overflow-hidden"
                      >
                        <CardContent className="p-0">
                          <div className="flex items-stretch">
                            {/* Date Block */}
                            <div className="flex-shrink-0 w-28 bg-gradient-to-br from-amber-400 to-amber-500 text-white p-4 flex flex-col items-center justify-center">
                              <div className="text-4xl font-bold">
                                {new Date(event.event_date).getDate()}
                              </div>
                              <div className="text-sm uppercase font-medium">
                                {new Date(event.event_date).toLocaleDateString('fr-FR', { month: 'short' })}
                              </div>
                              <div className="text-xs opacity-80 mt-1">
                                {new Date(event.event_date).getFullYear()}
                              </div>
                            </div>
                            {/* Event Details */}
                            <div className="flex-1 p-5">
                              <h3 className="font-heading font-bold text-xl text-neutral-800 mb-2">{event.title}</h3>
                              <p className="text-neutral-600 mb-4">{event.description}</p>
                              <div className="flex flex-wrap gap-4 text-sm text-neutral-500">
                                <span className="flex items-center gap-1.5 bg-neutral-100 px-3 py-1.5 rounded-full">
                                  <Calendar size={14} />
                                  {event.event_time}
                                </span>
                                <span className="flex items-center gap-1.5 bg-neutral-100 px-3 py-1.5 rounded-full">
                                  📍 {event.location}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-3xl">
                    <Calendar className="w-16 h-16 text-amber-300 mx-auto mb-4" />
                    <p className="text-neutral-600 font-medium">Aucun événement programmé.</p>
                    <p className="text-sm text-neutral-400 mt-2">Les événements seront bientôt annoncés.</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Galerie Tab */}
            <TabsContent value="galerie">
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-1.5 h-10 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-neutral-800">
                    Galerie Photos
                  </h2>
                </div>

                {loading ? (
                  <div className="flex justify-center py-16">
                    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
                  </div>
                ) : gallery.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {gallery.map((item) => (
                      <div 
                        key={item.id} 
                        className="group relative aspect-square cursor-pointer rounded-2xl overflow-hidden shadow-lg shadow-neutral-200/50 hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300"
                        onClick={() => setSelectedImage(item)}
                      >
                        <img 
                          src={getImageUrl(item.image_url)} 
                          alt={item.title || 'Photo de Gandiaye'}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          onError={(e) => { 
                            e.target.src = 'https://via.placeholder.com/400x400?text=Image'; 
                          }}
                        />
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                          <div className="absolute bottom-0 left-0 right-0 p-4">
                            <p className="text-white font-medium text-sm line-clamp-2">{item.title}</p>
                            {item.category && (
                              <span className="inline-block mt-2 px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded text-white/80 text-xs">
                                {item.category}
                              </span>
                            )}
                          </div>
                          {/* Zoom Icon */}
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                              <Eye className="w-6 h-6 text-white" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl">
                    <Image className="w-16 h-16 text-blue-300 mx-auto mb-4" />
                    <p className="text-neutral-600 font-medium">Aucune photo dans la galerie.</p>
                    <p className="text-sm text-neutral-400 mt-2">Les photos seront ajoutées prochainement.</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos">
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-1.5 h-10 bg-gradient-to-b from-red-400 to-red-600 rounded-full" />
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-neutral-800">
                    Médiathèque Vidéo
                  </h2>
                </div>

                {loading ? (
                  <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-red-200 border-t-red-600 rounded-full animate-spin" /></div>
                ) : videos.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {videos.map((video) => (
                      <Card key={video.id} className="group bg-white rounded-2xl overflow-hidden border-0 shadow-lg shadow-neutral-200/50 hover:shadow-2xl hover:shadow-red-500/10 transition-all duration-500 hover:-translate-y-2 cursor-pointer" onClick={() => setSelectedVideo(video)} data-testid={`video-card-${video.id}`}>
                        <div className="relative aspect-video overflow-hidden bg-neutral-900">
                          {video.thumbnail_url ? (
                            <img src={getImageUrl(video.thumbnail_url)} alt={video.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-neutral-800 to-neutral-900">
                              <Video className="w-16 h-16 text-neutral-600" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                            <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                              <Play size={24} className="text-red-600 ml-1" fill="currentColor" />
                            </div>
                          </div>
                          {video.video_type && (
                            <span className="absolute top-3 left-3 px-2 py-1 bg-black/60 backdrop-blur-sm rounded text-white text-xs font-medium">
                              {video.video_type === 'youtube' ? 'YouTube' : video.video_type === 'vimeo' ? 'Vimeo' : 'Vidéo'}
                            </span>
                          )}
                        </div>
                        <CardContent className="p-5">
                          <span className="px-3 py-1 bg-gradient-to-r from-red-100 to-rose-100 text-red-700 text-xs font-semibold rounded-full uppercase tracking-wide">
                            {video.category || 'Général'}
                          </span>
                          <h3 className="font-heading font-bold text-lg text-neutral-800 mt-3 mb-1 line-clamp-2 group-hover:text-red-600 transition-colors">
                            {video.title}
                          </h3>
                          {video.description && (
                            <p className="text-neutral-500 text-sm line-clamp-2">{video.description}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-gradient-to-br from-red-50 to-rose-50 rounded-3xl">
                    <Video className="w-16 h-16 text-red-300 mx-auto mb-4" />
                    <p className="text-neutral-600 font-medium">Aucune vidéo pour le moment.</p>
                    <p className="text-sm text-neutral-400 mt-2">Les vidéos seront ajoutées prochainement.</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Video Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-0 rounded-2xl overflow-hidden">
          {selectedVideo && (
            <div className="relative">
              <button 
                onClick={() => setSelectedVideo(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              >
                <X size={20} />
              </button>
              <div className="aspect-video">
                {selectedVideo.video_type === 'youtube' ? (
                  <iframe src={(() => { const m = selectedVideo.video_url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/); return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1` : selectedVideo.video_url; })()}
                    className="w-full h-full" allowFullScreen allow="autoplay; encrypted-media" title={selectedVideo.title} />
                ) : selectedVideo.video_type === 'vimeo' ? (
                  <iframe src={(() => { const m = selectedVideo.video_url.match(/vimeo\.com\/(\d+)/); return m ? `https://player.vimeo.com/video/${m[1]}?autoplay=1` : selectedVideo.video_url; })()}
                    className="w-full h-full" allowFullScreen allow="autoplay" title={selectedVideo.title} />
                ) : (
                  <video src={selectedVideo.video_url.startsWith('/api/uploads/') || selectedVideo.video_url.startsWith('/api/files/') ? `${BASE_URL}${selectedVideo.video_url}` : selectedVideo.video_url}
                    controls autoPlay className="w-full h-full" poster={selectedVideo.thumbnail_url ? getImageUrl(selectedVideo.thumbnail_url) : undefined} />
                )}
              </div>
              <div className="p-6 bg-gradient-to-t from-black/80 to-transparent">
                <h3 className="text-white font-heading font-bold text-xl mb-1">{selectedVideo.title}</h3>
                {selectedVideo.description && <p className="text-white/70 text-sm">{selectedVideo.description}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-0 rounded-2xl overflow-hidden">
          {selectedImage && (
            <div className="relative">
              <button 
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              >
                <X size={20} />
              </button>
              <img 
                src={getImageUrl(selectedImage.image_url)} 
                alt={selectedImage.title}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <h3 className="text-white font-heading font-bold text-xl mb-1">{selectedImage.title}</h3>
                {selectedImage.description && (
                  <p className="text-white/70 text-sm">{selectedImage.description}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default MediasPage;
